import { faEnvelope, faUserPlus } from '@fortawesome/pro-light-svg-icons';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { Toast, LoadingIndicator } from '@ygtechnologies/bygr-frontend-component-library';
import { Button, ButtonBar, ConfirmButton } from '@ygtechnologies/rubus-ui';
import { CustomLink } from '@/components/navigation';
import { uniqBy } from 'lodash-es';
import React, { useState } from 'react';
import ProjectMemberPermissionsList from '@/seller/components/project/UserManagement/ProjectMemberPermissionsList';
import ProjectPermissionsEditDialog from '@/seller/components/project/UserManagement/ProjectPermissionsEditDialog';
import { resendProfileInvite } from '@/services/profiles';
import { getProjectAcl, removeProjectMember } from '@/services/projects';
import { ProjectMemberDetails } from '@/types';
import { useAsyncState } from '@/utils/hooks';
import ProjectInviteIntro from '../ProjectInvite/ProjectInviteIntro';
import theme from './ProjectUserManagement.module.scss';
import { SelectCompanyMenu } from './SelectCompanyMenu';
import RemoveProjectMemberDialog from './RemoveProjectMemberDialog/RemoveProjectMemberDialog';

interface EditInfo {
    memberDetails: ProjectMemberDetails;
    editType: 'permissions';
}

interface ProjectUserManagementProps {
    projectId: number;
}

const ProjectUserManagement: React.FC<ProjectUserManagementProps> = ({ projectId }) => {
    const [editInfo, setEditInfo] = useState<EditInfo | null>(null);
    const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
    const [removeUser, setRemoveUser] = useState<ProjectMemberDetails | null>(null);
    const [members, , reloadMembers] = useAsyncState(() => getProjectAcl(projectId), [projectId], null);
    const [usersActiveTab, setUsersActiveTab] = useState<number | null>(null);
    const companyNames = uniqBy(
        members?.map((x) => {
            return {
                id: x.profileId,
                companyName: x.profileName,
            };
        }),
        'id'
    );
    const handleRemoveMemberClick = async (memberDetails: ProjectMemberDetails) => {
        await removeProjectMember(memberDetails.projectMemberId);
        reloadMembers();
        Toast.success(<Trans>User is removed</Trans>);
    };
    const handleInviteMemberClick = async (memberDetails: ProjectMemberDetails) => {
        if (memberDetails.inviteId) {
            await resendProfileInvite(memberDetails.inviteId);
            Toast.success(<Trans>Invitation resent</Trans>);
        } else {
            Toast.error(<Trans>User invitation is invalid. Try manually deleting and reinviting</Trans>);
        }
    };

    const handleEditDialogClose = () => {
        setEditInfo(null);
    };

    const handleEditDialogAccept = () => {
        reloadMembers();
    };

    const handleReinviteAll = async () => {
        let containsError = false;
        let oneOrMoreSuccess = false;
        await members
            ?.filter((y) => y.status.localeCompare('Active'))
            .forEach((member) => {
                if (member.inviteId) {
                    resendProfileInvite(member.inviteId);
                    oneOrMoreSuccess = true;
                } else {
                    containsError = true;
                    Toast.error(
                        '- ' +
                            member.displayName +
                            ': ' +
                            t`User invitation is invalid. Try manually deleting and reinviting`,
                        { autoClose: false }
                    );
                }
            });
        if (containsError) {
            if (oneOrMoreSuccess) {
                Toast.warn(
                    <Trans>
                        Invitations has been sent, but there was users with errors. Please try manually deleting and
                        reinviting
                    </Trans>
                );
            } else {
                Toast.error(<Trans>No invitations has been sent. Please try manually deleting and reinviting</Trans>);
            }
        } else {
            Toast.success(<Trans>Invitations has been sent</Trans>);
        }
        reloadMembers();
    };

    if (!members) {
        return <LoadingIndicator />;
    }

    const displayMembers: ProjectMemberDetails[] =
        usersActiveTab !== null
            ? usersActiveTab === -1
                ? members.filter((y) => y.status.localeCompare('Active'))
                : members.filter((y) => usersActiveTab === y.profileId)
            : members;

    return (
        <div>
            <div>
                <ProjectPermissionsEditDialog
                    projectId={projectId}
                    show={(editInfo && editInfo.editType === 'permissions') || false}
                    memberDetails={editInfo ? editInfo.memberDetails : null}
                    onClose={handleEditDialogClose}
                    onAccept={handleEditDialogAccept}
                />
                {removeUser && (
                    <RemoveProjectMemberDialog
                        onRemoved={handleRemoveMemberClick}
                        projectMember={removeUser}
                        isOpen={!!removeUser}
                        toggle={() => setRemoveUser(null)}
                    />
                )}

                <ButtonBar className={theme.userMenuBar}>
                    <SelectCompanyMenu
                        companyList={companyNames.sort((a, b) => (a.companyName > b.companyName ? 1 : -1))}
                        activeTab={usersActiveTab}
                        setActiveTab={setUsersActiveTab}
                    />
                    <ButtonBar.Right>
                        <Button
                            as={CustomLink}
                            to={`/projects/${projectId}/users/invite`}
                            variant="tertiary"
                            icon={faUserPlus}>
                            <Trans>Add a user</Trans>
                        </Button>
                    </ButtonBar.Right>
                </ButtonBar>
                {usersActiveTab === -1 && members.filter((y) => y.status.localeCompare('Active')).length > 0 && (
                    <div className={theme.invitationContainer}>
                        <ConfirmButton
                            variant="tertiary"
                            dialogBody={
                                <>
                                    <Trans>Are you sure you want to resend invitations to the following?</Trans>
                                    <hr />
                                    <table className={theme.invitationList}>
                                        <tbody>
                                            {members
                                                .filter((y) => y.status.localeCompare('Active'))
                                                .map((member) => {
                                                    return (
                                                        <tr key={member.projectMemberId}>
                                                            <td>{member.displayName}</td>
                                                            <td className={theme.mutedText}>{member.profileName}</td>
                                                        </tr>
                                                    );
                                                })}
                                        </tbody>
                                    </table>
                                </>
                            }
                            dialogTitle={<Trans>Resend invitation</Trans>}
                            confirmButton={{
                                children: <Trans>Send</Trans>,
                            }}
                            icon={faEnvelope}
                            iconPosition="left"
                            onConfirm={handleReinviteAll}>
                            <Trans>Resend invitations</Trans>
                        </ConfirmButton>
                    </div>
                )}

                <ProjectMemberPermissionsList
                    projectId={projectId}
                    members={displayMembers}
                    onEditPermissionsClick={(member) => setEditInfo({ memberDetails: member, editType: 'permissions' })}
                    onRemoveClick={handleRemoveMemberClick}
                    onInviteClick={handleInviteMemberClick}
                />
            </div>
            <ProjectInviteIntro onCancel={() => setInviteDialogOpen(false)} isOpen={inviteDialogOpen} />
        </div>
    );
};

export default ProjectUserManagement;
